import { useEffect, useState } from 'react';
import { Avatar } from '@immo/ui-kit';
import { resolveAvatarUrl } from '@/lib/media/avatar-url-cache';

type MemberAvatarProps = {
  readonly workspaceId: string;
  readonly avatarMediaId: string | null;
  readonly displayName: string;
  readonly seed?: string;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly title?: string;
  readonly 'aria-label'?: string;
  readonly role?: string;
  readonly className?: string;
};

export function MemberAvatar({
  workspaceId,
  avatarMediaId,
  displayName,
  seed,
  size = 'md',
  title,
  'aria-label': ariaLabel,
  role,
  className,
}: MemberAvatarProps) {
  const [src, setSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    let active = true;

    if (avatarMediaId === null) {
      setSrc(undefined);

      return;
    }

    void resolveAvatarUrl(workspaceId, avatarMediaId).then((url) => {
      if (active) setSrc(url ?? undefined);
    });

    return () => {
      active = false;
    };
  }, [workspaceId, avatarMediaId]);

  return (
    <Avatar
      initials={initials(displayName)}
      seed={seed ?? displayName}
      size={size}
      src={src}
      title={title}
      aria-label={ariaLabel}
      role={role}
      className={className}
    />
  );
}

/**
 * Exported for the surfaces that render a bare `Avatar` because they hold a name
 * but no `avatarMediaId` — the Deal-Überblick's Zuständig cell (IMM-726.1). The
 * derivation lives here, beside the component that has always used it, so the
 * two never disagree about what a name's initials are.
 */
export function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '?';

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}
